import './styles.scss'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingOutlined } from '@ant-design/icons'
import { Col, Modal, Row } from 'antd'
import Skeleton from 'react-loading-skeleton'

import B2becTable from '@/components/B2becTable'
import CustomButton from '@/components/CustomButton'
import FormatStatus from '@/components/FormatStatus'
import { orderService } from '@/services'
import useAsync from '@/hooks/useAsync'
import useDisableBodyScroll from '@/hooks/useDisableBodyScroll'
import { ASYNC_STATUS } from '@/libs/constants'
import { hasEmptyElements, isValidArray } from '@/libs/utils/array'
import { formatDate, getFormatType } from '@/libs/utils/dateUtils'
import { downloadMultipleInvoicesZipFile, getInvoices } from '@/libs/utils/PDFHandler'
import { isStringNotEmpty } from '@/libs/utils/stringUltis'
import { toast } from '@/libs/utils/toast'

import invoiceData from './invoiceData'
import tableColumn from './tableColumns'

interface DownloadModalProps {
  showModal?: boolean
  setShowModal?: (...args: unknown[]) => unknown
  orderNumber?: string
  orderDate?: string
  referenceNumber?: string
  orderStatus?: string
  hasDownloadInvoicePermission?: boolean
}

function DownloadModal(props: DownloadModalProps) {
  const {
    showModal = false,
    setShowModal,
    orderNumber = '',
    orderDate = 'N/A',
    referenceNumber = 'N/A',
    orderStatus = 'N/A',
    hasDownloadInvoicePermission,
  } = props

  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState(true)
  const [tableData, setTableData] = useState(invoiceData)
  const [selectedInvoices, setSelectedInvoices] = useState([])

  useDisableBodyScroll(showModal)

  useEffect(() => {
    const getProductList = async () => {
      try {
        const data = await orderService.getProductsListByOrderNumber(orderNumber)

        if (isValidArray(data)) {
          setTableData(
            data.map((item, index) => ({
              index,
              ...item,
            })),
          )
        } else {
          toast.error({ message: 'notification.error.loadInvoicesInfo' })

          setTableData([])
        }
      } catch (error) {
        console.error('Error loading invoices info:', error)
        toast.error({ message: 'notification.error.loadInvoicesInfo' })

        setTableData([])
      } finally {
        setIsLoading(false)
      }
    }

    getProductList()
  }, [orderNumber])

  const renderTitle = () => (
    <div className="invoices-download-modal__title">
      {t('orders.history.invoiceDownloadModal.title')}
    </div>
  )

  const renderOrderNumber = () => (
    <div className="invoices-download-modal__order-number">
      <p>
        {t('orders.history.invoiceDownloadModal.orderNumber.title')}{' '}
        {isLoading ? <Skeleton width={90} /> : orderNumber || 'N/A'}
      </p>
      <p>{t('orders.history.invoiceDownloadModal.orderNumber.description')}</p>
    </div>
  )

  const renderOrderDate = () => (
    <div className="invoices-download-modal__order-date">
      <p>{t('orders.history.invoiceDownloadModal.orderDate')}</p>
      <p>
        {isLoading ? <Skeleton /> : (
          formatDate(orderDate, getFormatType().default) || 'N/A'
        )}
      </p>
    </div>
  )

  const renderReferenceNumber = () => (
    <div className="invoices-download-modal__reference-number">
      <p>{t('orders.history.invoiceDownloadModal.referenceNumber')}</p>
      <p>{isLoading ? <Skeleton /> : referenceNumber || 'N/A'}</p>
    </div>
  )

  const renderStatus = () => (
    <div className="invoices-download-modal__status">
      <p>{t('orders.history.invoiceDownloadModal.status')}</p>
      <p>
        {isLoading ? <Skeleton width={90} /> : <FormatStatus status={orderStatus} />}
      </p>
    </div>
  )

  const onCheckboxChange = (checked, invoiceNumber) => {
    const index = tableData.findIndex(
      (invoice) => invoice.invoiceNumber === invoiceNumber,
    )
    const newSelectedInvoice = [...selectedInvoices].filter(
      (invoice) => invoice !== invoiceNumber,
    )
    if (checked) {
      newSelectedInvoice.push(invoiceNumber)
    }
    setSelectedInvoices(newSelectedInvoice)

    tableData[index].checked = checked
    setTableData([...tableData])
  }

  const renderInvoiceTable = () => (
    <B2becTable
      boxShadow={false}
      className="invoices-download-modal__table"
      columns={tableColumn(isLoading, onCheckboxChange)}
      dataSource={tableData}
      pagination={false}
      style={{ maxHeight: '40vh', marginBottom: 9 }}
      rowKey={({ index, invoiceNumber, products }) =>
        `${index}-${invoiceNumber}-${
          products
            .map((item) => item.materialNumber)
            .join('-')
        }`}
    />
  )

  const { execute, status, value } = useAsync(
    async () => getInvoices(selectedInvoices),
    false,
  )

  const isDownloading = status === ASYNC_STATUS.PENDING

  useEffect(() => {
    if (status === ASYNC_STATUS.SUCCESS) {
      const dataArrayToDownload = Array.isArray(value)
        ? value.filter(
          (item) =>
            isStringNotEmpty(item.fileName)
            && isStringNotEmpty(item.file)
            && isStringNotEmpty(item.contentType),
        )
        : []
      if (dataArrayToDownload.length > 0 && hasDownloadInvoicePermission) {
        downloadMultipleInvoicesZipFile(dataArrayToDownload)
      } else {
        toast.error({ message: 'notification.error.downloadInvoice' })
      }
    } else if (status === ASYNC_STATUS.ERROR) {
      toast.error({ message: 'notification.error.downloadInvoice' })
    }
  }, [status, value, hasDownloadInvoicePermission])

  const onCancel = () => setShowModal?.(false)

  const onDownloadInvoices = useCallback(() => {
    if (hasDownloadInvoicePermission) {
      execute()
    }
  }, [execute, hasDownloadInvoicePermission])

  const shouldDisableDownloadButton = useMemo(
    () =>
      isLoading
      || isDownloading
      || hasEmptyElements(selectedInvoices)
      || !hasDownloadInvoicePermission,
    [hasDownloadInvoicePermission, isDownloading, isLoading, selectedInvoices],
  )

  const renderFooter = () => (
    <Row justify="space-between">
      <Col lg={12} md={12} sm={24} xl={12} xs={24}>
        <CustomButton
          className="invoices-download-modal__cancel"
          type="ghost"
          onClick={onCancel}
        >
          {t('orders.history.invoiceDownloadModal.cancel')}
        </CustomButton>
      </Col>
      <Col lg={12} md={12} sm={24} xl={12} xs={24}>
        <CustomButton
          className="invoices-download-modal__download"
          disabled={shouldDisableDownloadButton}
          onClick={onDownloadInvoices}
        >
          {isDownloading && <LoadingOutlined style={{ marginRight: 8 }} />}
          {t('orders.history.invoiceDownloadModal.download')}
        </CustomButton>
      </Col>
    </Row>
  )

  return (
    <Modal
      className="invoices-download-modal"
      closable={false}
      footer={renderFooter()}
      open={showModal}
      width={700}
      onCancel={onCancel}
    >
      {renderTitle()}
      {renderOrderNumber()}
      <div className="invoices-download-modal__order-date-reference-number__wrapper">
        {renderOrderDate()}
        {renderReferenceNumber()}
      </div>
      {renderStatus()}
      {renderInvoiceTable()}
    </Modal>
  )
}

export default DownloadModal
